import Connections from '../../connections';
import {
  action, observable,
} from 'mobx';
import {
  uploadFileURL,
} from '../../axios/requests';
import {
  uploadFile,
} from '../../helperFunctions/uploadService';
import { IGeneralAdStore, IGeneralAd } from '../storeInterfaces/generalAdInterface';
import { generalAdsUrl, activateAdsUrl, deactivateAdsUrl, getGeneralAdByIdUrl } from '../../axios/generalAdsRequests';
import { convertCountriesToISOCodeString } from '../../constants/countries';
import {
  ICountry,
} from '../storeInterfaces/authInterface';
import { RequestStatus } from 'mobx-store';

export class GeneralAdsStore implements IGeneralAdStore {

  @observable generalAdsList: IGeneralAd[] = [];

  @observable selectedGeneralAd: any = {};

  @observable loading: boolean = false;

  @observable filesConflictError: boolean = false;

  @observable getGeneralAdsRequestStatus: RequestStatus = RequestStatus.IDLE;

  @action.bound
  public async getAllGeneralAds(countries: ICountry[]): Promise<void> {
    try {
      this.loading = true;
      this.getGeneralAdsRequestStatus = RequestStatus.LOADING;
      const params = this.formRequestParams(countries);
      const response = await Connections.wadiDeglaAxios.httpGet(generalAdsUrl(), {params});
      this.generalAdsList = response.data;
      this.getGeneralAdsRequestStatus = RequestStatus.SUCCESS;
    } catch (error) {
      this.getGeneralAdsRequestStatus = RequestStatus.FAILED;
      console.log(error);
      throw(error)
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  public async activateGeneralAd(id: number, availableCountries: ICountry[]): Promise<void> {
    try {
      this.loading = true;
      const countries = availableCountries.map(country => country.countryId);
      const params = {
        availableCountries: countries
      }
      await Connections.wadiDeglaAxios.httpPut(activateAdsUrl(id), {params});
      await this.getAllGeneralAds(availableCountries);
    } catch (error) {
      console.log(error);
      throw(error)
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  public async deactivateGeneralAd(id: number, availableCountries: ICountry[]): Promise<void> {
    try {
      this.loading = true;
      await Connections.wadiDeglaAxios.httpPut(deactivateAdsUrl(id), {});
      await this.getAllGeneralAds(availableCountries);
    } catch (error) {
      console.log(error);
      throw(error)
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  public async createGeneralAd(generalAd: IGeneralAd, availableCountries: ICountry[]): Promise<void> {
    try {
      this.loading = true;
      const newAd = await this.restructureGeneralAdBeforeSave(generalAd);
      const countriesIds = availableCountries.map(country => {
        return {
          countryId: country.countryId
        }
      });
      await Connections.wadiDeglaAxios.httpPost(generalAdsUrl(), {...newAd, availableCountries: countriesIds});
      await this.getAllGeneralAds(availableCountries);
    } catch (error) {
      if(error && error.response && error.response.status === 400) {
        this.filesConflictError = true;
      }
      throw(error)
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  public async updateGeneralAd(generalAd: IGeneralAd, availableCountries: ICountry[]): Promise<void> {
    try {
      this.loading = true;
      generalAd.isActive = this.selectedGeneralAd.isActive;
      const newAd = await this.restructureGeneralAdBeforeSave(generalAd);
      const countriesIds = availableCountries.map(country => {
        return {
          countryId: country.countryId
        }
      });
      await Connections.wadiDeglaAxios.httpPut(generalAdsUrl(), {...newAd, availableCountries: countriesIds});
      await this.getAllGeneralAds(availableCountries);
    } catch (error) {
      if(error && error.response && error.response.status === 400) {
        this.filesConflictError = true;
      }
      throw(error)
    } finally {
      this.loading = false;
    }
  }

  private async restructureGeneralAdBeforeSave(generalAd: IGeneralAd) {
    const widgetImages = await this.uploadAdImages([generalAd.widgetImageURL_ar[0], generalAd.widgetImageURL_en[0]]);
    let newAd: any = {
      title: generalAd.title,
      isActive: generalAd.isActive,
      widgetImageURL_ar: widgetImages[0],
      widgetImageURL_en: widgetImages[1],
    };
    if((!!generalAd.videoURL[0] && !generalAd.imageURL_ar[0] && !generalAd.imageURL_en[0]) || (!generalAd.videoURL[0] && !!generalAd.imageURL_ar[0] && !!generalAd.imageURL_en[0])) {
      if(!!generalAd.videoURL[0]) {
        const video = await this.uploadAdVideo(generalAd.videoURL);
        newAd.videoURL = video[0].videoUrl;
      }
      if(!!generalAd.imageURL_ar[0] && !!generalAd.imageURL_en[0]) {
        const images = await this.uploadAdImages([generalAd.imageURL_ar[0], generalAd.imageURL_en[0]]);
        newAd.imageURL_ar = images[0];
        newAd.imageURL_en = images[1];
      }  
    } else {
      this.filesConflictError = true;
      throw({error: 'error while uploading'});
    }
    if(!!generalAd.generalAdId) {
      newAd.id = generalAd.generalAdId;
    }

    return newAd;
  }

  @action.bound
  public async getGeneralAdById(id: number): Promise<void> {
    try {
      this.loading = true;
      const response = await Connections.wadiDeglaAxios.httpGet(getGeneralAdByIdUrl(id));
      this.selectedGeneralAd = this.restructureGeneralAd(response.data);
    } catch (error) {
      console.log(error);
      throw(error);
    } finally {
      this.loading = false;
    }
  }

  private uploadAdVideo(videos: any[]) {
    const promises = [];
    const allVideos: any[] = [];
    for (const video of videos) {
      if (video && video.preview) {
        promises.push(
          uploadFile(uploadFileURL, video)
            .then((res: any) => {
              allVideos.push({
                videoUrl: res.data.downloadURL,
              });
            }),
        );
      } else {
        allVideos.push(video);
      }
    }
    return Promise.all(promises)
      .then(() => {
        console.log('done');
        return allVideos;
      }).catch((error) => {
        console.log('error');
        return allVideos;
      });
  }

  private uploadAdImages(images: any[]) {
    const promises = [];
    const allImages: any[] = [];
    for (const image of images) {
      if (image && image.preview) {
        promises.push(
          uploadFile(uploadFileURL, image)
            .then((res: any) => {
              allImages.push(res.data.downloadURL);
            }),
        );
      } else {
        allImages.push(image.imageUrl);
      }
    }
    return Promise.all(promises)
      .then(() => {
        console.log('done');
        return allImages;
      }).catch((error) => {
        console.log('error');
        return allImages;
      });
  }

  private restructureGeneralAd(generalAd: IGeneralAd): any {
    
    return {
      generalAdId: generalAd.generalAdId,
      imageURL_ar: (generalAd.imageURL_ar ? [{
        imageUrl: generalAd.imageURL_ar, key: 0, type: 'image',
      }] : []),
      imageURL_en: (generalAd.imageURL_ar ? [{
        imageUrl: generalAd.imageURL_en, key: 1, type: 'image',
      }] : []),
      videoURL: (generalAd.videoURL ? [{
        videoUrl: generalAd.videoURL, key: 2, type: 'video',
      }] : []),
      widgetImageURL_ar: (generalAd.widgetImageURL_ar ? [{
        imageUrl: generalAd.widgetImageURL_ar, key: 3, type: 'image',
      }] : []),
      widgetImageURL_en: (generalAd.widgetImageURL_en ? [{
        imageUrl: generalAd.widgetImageURL_en, key: 4, type: 'image',
      }] : []),
      title: (generalAd.title) ? generalAd.title : '',
      isActive: generalAd.isActive,
    };
  }

  private formRequestParams(countries: ICountry[]) {
    
    const availableCountries = convertCountriesToISOCodeString(countries);

    const params = {
      availableCountries,
    };

    return params;
  }

}